// Location-primer data layer for SeaState.
//
// Assembles the handful of standing facts that describe *where* a site is — the
// named seabed feature it sits on, nearby offshore infrastructure, protected
// zones that constrain operations, the nearest port, and what the month usually
// brings — into a small typed record, then composes that record into a short
// prose paragraph. This module does NO rendering beyond returning an HTML string
// and touches NO DOM: data in, data + prose out.
//
// Every source is fetched straight from the browser against a public,
// CORS-enabled endpoint, and every source degrades independently: a dead or
// empty upstream makes that fact `null`, it never throws into a blank page.
// `fetchPrimer` runs all sources under a single `Promise.allSettled`, and each
// source's own parser tolerates a partial or malformed response.

import { convLen, fmtKm, getUnitSystem, lenUnit } from "../ui/units.js";

export interface PortFact {
  name: string;
  distanceNm: number;
  bearing: string;
}

export interface LeaseFact {
  count: number;
  /** nearest = "OCS-A 0499 (Company)", or null when none within range. */
  nearest: string | null;
}

export interface WhaleFact {
  location: string;
  period: string;
}

export interface PlatformFact {
  /** Standing oil & gas platforms within range. */
  count: number;
  /** Distance to the nearest, nm; null when the geometry didn't come back. */
  distanceNm: number | null;
  bearing: string | null;
}

/**
 * The seasonal normal for a month at a point: the middle half of HOURLY
 * conditions, from ERA5 reanalysis.
 *
 * HOURLY IS LOAD-BEARING AND WAS NOT ALWAYS SO. This used to fetch
 * `wind_speed_10m_max` and `wave_height_max` — DAILY MAXIMA — and take p25-p75
 * across those days. The prose then said the area "typically sees 10-14 kn",
 * which is not what that number means: it is the middle half of daily PEAKS,
 * and typical hourly wind is well below it.
 *
 * Buried in a sentence that was an overstatement. Drawn as a band behind an
 * hourly forecast line it would have been a trap — the forecast would sit under
 * the band most of the time purely because most hours are not the day's maximum,
 * and the honest reading "this window is normal" would look like "this window is
 * unusually calm". Same family as the peak-vs-energy wave-period defect: two
 * quantities with the same name and different meanings, plotted on one axis.
 *
 * So the percentiles now describe the same quantity the charts plot, and can be
 * compared with them directly.
 */
export interface ClimFact {
  /** p25 of hourly wind speed, knots. */
  windP25Kn: number;
  /** p75 of hourly wind speed, knots. */
  windP75Kn: number;
  /** p25 of hourly significant wave height, metres. */
  waveP25M: number | null;
  /** p75 of hourly significant wave height, metres. */
  waveP75M: number | null;
  /** How many years of that month answered for WIND — the sample, stated. */
  years: number;
  /** Same for WAVE, counted separately: the two archives are different
   *  datasets and one can be short where the other is complete. */
  waveYears: number;
  /** The window actually used, inclusive. */
  firstYear: number;
  lastYear: number;
  monthName: string;
  /**
   * The window split in half, each summarised as a percentile sketch of hourly
   * values — index i is the i-th percentile, 0..100.
   *
   * A sketch rather than the raw hours because the raw hours are 22,000 numbers
   * per variable and this is cached in localStorage; and a sketch rather than a
   * fixed set of thresholds because the threshold that matters is the VESSEL's
   * limit, which changes when the operator re-arms. From percentiles you can
   * answer any threshold; from thresholds you cannot answer any percentile.
   */
  early: ClimHalf | null;
  late: ClimHalf | null;
}

/** One half of the climatology window, as percentile sketches. */
export interface ClimHalf {
  firstYear: number;
  lastYear: number;
  /** p0..p100 of hourly wind speed, knots. */
  windPct: number[];
  /** p0..p100 of hourly significant wave height, metres. Empty if unavailable. */
  wavePct: number[];
}

/**
 * The share of hours at or above `threshold`, as a percentage, read off a
 * percentile sketch.
 *
 * Linear interpolation between the two bracketing percentiles. Returns null for
 * an empty sketch rather than 0 — "no data" and "never exceeded" are opposite
 * answers and a deck crew must not be shown the reassuring one by accident.
 */
export function exceedancePct(pct: number[], threshold: number): number | null {
  const n = pct.length;
  if (n < 2 || !Number.isFinite(threshold)) return null;
  if (threshold <= pct[0]) return 100;
  if (threshold > pct[n - 1]) return 0;
  for (let i = 1; i < n; i++) {
    if (pct[i] >= threshold) {
      const lo = pct[i - 1];
      const hi = pct[i];
      // Percentile of the threshold, then the complement.
      const frac = hi === lo ? 0 : (threshold - lo) / (hi - lo);
      const percentile = ((i - 1 + frac) / (n - 1)) * 100;
      return Math.max(0, Math.min(100, 100 - percentile));
    }
  }
  return 0;
}

export interface SeabedNeighbor {
  /** Display name including its generic term, e.g. "West Florida Escarpment". */
  name: string;
  /** Generic feature term, e.g. "Escarpment" — keys the glossary. */
  type: string;
  distanceNm: number;
  bearing: string;
}

export interface PrimerFacts {
  /** Named undersea feature, e.g. "Blake Plateau"; null when none is charted. */
  feature: string | null;
  /** Generic term of `feature` ("Plateau", "Shelf"…); keys the glossary. */
  featureType: string | null;
  /** Named water body, e.g. "Gulf of Mexico"; null when unresolved. */
  sea: string | null;
  /** Charted seabed features within 100 nm, nearest first (max 3). */
  nearbySeabed: SeabedNeighbor[];
  /** US offshore-wind leases within 50 km; null when the source was unreachable. */
  windLeases: LeaseFact | null;
  /** US oil & gas platforms within 50 km (count + nearest distance); null when unreachable. */
  platforms: PlatformFact | null;
  /** Right-whale seasonal zone the point sits inside; null when outside OR unavailable. */
  whale: WhaleFact | null;
  /** True when the whale source answered — so a null `whale` means genuinely outside a zone. */
  whaleChecked: boolean;
  /** Nearest commercial port (World Port Index); null when none found or unavailable. */
  port: PortFact | null;
  /** Typical wind/sea for the calendar month; null when no history came back. */
  clim: ClimFact | null;
}

const MONTH_NAMES: readonly string[] = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

// 16-point compass, clockwise from North; index = round(bearing / 22.5) % 16.
const COMPASS_16: readonly string[] = [
  "N",
  "NNE",
  "NE",
  "ENE",
  "E",
  "ESE",
  "SE",
  "SSE",
  "S",
  "SSW",
  "SW",
  "WSW",
  "W",
  "WNW",
  "NW",
  "NNW",
] as const;

const COMPASS_WORD: Readonly<Record<string, string>> = {
  N: "north",
  NNE: "north-northeast",
  NE: "northeast",
  ENE: "east-northeast",
  E: "east",
  ESE: "east-southeast",
  SE: "southeast",
  SSE: "south-southeast",
  S: "south",
  SSW: "south-southwest",
  SW: "southwest",
  WSW: "west-southwest",
  W: "west",
  WNW: "west-northwest",
  NW: "northwest",
  NNW: "north-northwest",
};

// --- JSON narrowing helpers -------------------------------------------------
//
// Every `res.json()` is `unknown`. We narrow it lazily through small guards
// rather than asserting a shape, so a partial or unexpected response falls back
// to a benign default instead of throwing.

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** ArcGIS `features` array from a query response, or null. */
function featuresOf(response: unknown): unknown[] | null {
  const record = asRecord(response);
  return record ? asArray(record.features) : null;
}

/** ArcGIS feature `attributes` record, or null. */
function attributesOf(feature: unknown): Record<string, unknown> | null {
  const record = asRecord(feature);
  return record ? asRecord(record.attributes) : null;
}

// --- Geometry ---------------------------------------------------------------

const EARTH_RADIUS_NM = 3440.065; // nautical miles

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Great-circle distance between two lat/lon points, in nautical miles. */
function haversineNm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_NM * 2 * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Initial great-circle bearing from point 1 to point 2, as a 16-point code. */
function bearing16(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): string {
  const dLon = toRad(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
  const deg = (Math.atan2(y, x) * 180) / Math.PI;
  const idx = Math.round(((deg % 360) + 360) / 22.5) % 16;
  return COMPASS_16[idx];
}

// --- Statistics -------------------------------------------------------------

/** Linear-interpolation quantile of an ascending-sorted, non-empty array. */
function quantile(sortedAsc: number[], q: number): number {
  const n = sortedAsc.length;
  if (n === 0) return NaN;
  if (n === 1) return sortedAsc[0];
  const pos = q * (n - 1);
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return sortedAsc[lo];
  return sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * (pos - lo);
}

// --- Text -------------------------------------------------------------------

/** "CHARLESTON" → "Charleston"; "FERNANDINA BEACH" → "Fernandina Beach". */
function titleCase(name: string): string {
  return name
    .toLowerCase()
    .replace(/\b[a-z]/g, (c) => c.toUpperCase())
    .trim();
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// --- Fetch ------------------------------------------------------------------

async function getJson(url: string): Promise<unknown> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
  return (await res.json()) as unknown;
}

/** Fetch JSON, returning null on any network/parse failure (never throws). */
async function getJsonOrNull(url: string): Promise<unknown> {
  try {
    return await getJson(url);
  } catch {
    return null;
  }
}

function esriPoint(lon: number, lat: number): string {
  return encodeURIComponent(JSON.stringify({ x: lon, y: lat }));
}

// --- Source 1: named undersea feature + water body (Marine Regions) ---------
//
// getGazetteerRecordsByLatLong returns every gazetteer polygon covering the
// point, from many sources (EEZs, ecoregions, provinces…) and one row per name
// variant. Two facts come out of the one response:
//
//  · feature — records from the GEBCO Gazetteer of Undersea Feature Names (the
//    seabed-feature source), picking the record (MRGID) whose representative
//    point is nearest the query, i.e. the most local named feature. Bounding
//    boxes are not exposed here, so nearest-representative-point is the
//    deterministic choice. A second call resolves that MRGID to its canonical
//    preferred name, since the first response also carries transliterations
//    and alternative spellings under the same MRGID.
//
//  · sea — the named water body from the SeaVoX gazetteer, whose tiers nest
//    cleanly (sub-region ⊂ level 2 ⊂ level 1); the most local tier present
//    wins, so a Gulf of Mexico point says "Gulf of Mexico", not "Atlantic".

interface RegionFacts {
  feature: string | null;
  featureType: string | null;
  sea: string | null;
}

/** "Gulf Of Mexico" → "Gulf of Mexico"; "Northwest Atlantic Ocean (40W)" → cut. */
function cleanSeaName(name: string): string {
  return name
    .replace(/\s*\([^)]*\)\s*$/, "")
    .replace(/\b(Of|The|And)\b/g, (w) => w.toLowerCase())
    .trim();
}

const SEAVOX_TIERS: readonly string[] = [
  "SeaVoX SeaArea - sub-region",
  "SeaVoX SeaArea - level 2",
  "SeaVoX SeaArea - level 1",
] as const;

async function fetchRegion(lat: number, lon: number): Promise<RegionFacts> {
  const url =
    "https://www.marineregions.org/rest/getGazetteerRecordsByLatLong.json/" +
    `${lat.toFixed(4)}/${lon.toFixed(4)}/`;
  const response = await getJsonOrNull(url);
  const rows = asArray(response);
  if (!rows) return { feature: null, featureType: null, sea: null };

  let bestMrgid: number | null = null;
  let bestName: string | null = null;
  let bestType: string | null = null;
  let bestDist = Infinity;
  const seaByTier = new Map<string, string>();
  for (const row of rows) {
    const record = asRecord(row);
    if (!record) continue;
    const source = asString(record.gazetteerSource);
    const placeType = asString(record.placeType);
    const name = asString(record.preferredGazetteerName);
    if (!name) continue;

    if (placeType !== null && SEAVOX_TIERS.includes(placeType) && !seaByTier.has(placeType)) {
      seaByTier.set(placeType, cleanSeaName(name));
    }

    if (!source || !source.includes("GEBCO")) continue;
    const rlat = asFiniteNumber(record.latitude);
    const rlon = asFiniteNumber(record.longitude);
    const dist =
      rlat !== null && rlon !== null ? haversineNm(lat, lon, rlat, rlon) : 0;
    if (dist < bestDist) {
      bestDist = dist;
      bestName = name;
      bestType = placeType;
      bestMrgid = asFiniteNumber(record.MRGID);
    }
  }

  let sea: string | null = null;
  for (const tier of SEAVOX_TIERS) {
    const name = seaByTier.get(tier);
    if (name !== undefined && name !== "") {
      sea = name;
      break;
    }
  }

  if (bestMrgid === null) return { feature: bestName, featureType: bestType, sea };

  // Resolve the MRGID to its canonical preferred name; fall back to the name
  // variant we already have if that lookup fails.
  const canonical = await getJsonOrNull(
    `https://www.marineregions.org/rest/getGazetteerRecordByMRGID.json/${bestMrgid}/`,
  );
  const preferred = asString(asRecord(canonical)?.preferredGazetteerName);
  return { feature: preferred ?? bestName, featureType: bestType, sea };
}

// --- Source 1b: nearby charted seabed features (GEBCO gazetteer, NCEI) -------
//
// The IHO-IOC GEBCO Gazetteer of Undersea Feature Names, hosted by NOAA NCEI as
// an ArcGIS feature service in three geometry layers (points, lines, polygons).
// Marine Regions only reports features whose polygon COVERS the point, which
// says nothing on the open shelf between named features — this source answers
// "what named seabed features are around me?" We pull everything within 100 nm
// and measure distance ourselves: minimum point-to-segment distance on a local
// tangent plane (many features are sparse-vertex axis lines — the Yucatán Shelf
// line has two vertices — so vertex-only distance would be off by 100 nm).
// A polygon that contains the point counts as "on" it, distance zero.

const GEBCO_FS =
  "https://services2.arcgis.com/C8EMgrsFcRFL6LrL/arcgis/rest/services/" +
  "Undersea_Features/FeatureServer";
const SEABED_RADIUS_NM = 100;

interface SeabedFacts {
  /** Polygon feature the point is inside, if any. */
  on: { name: string; type: string } | null;
  nearby: SeabedNeighbor[];
}

/** "West Florida" + "Escarpment" → "West Florida Escarpment" (no doubling). */
function seabedDisplayName(name: string, type: string): string {
  const generic = type.replace(/\(s\)\s*$/, "").trim();
  return name.toLowerCase().endsWith(generic.toLowerCase())
    ? name
    : `${name} ${generic}`;
}

/** Even-odd ray cast over every ring — outer boundaries and holes together. */
function pointInRings(lat: number, lon: number, rings: number[][][]): boolean {
  let inside = false;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      if (
        yi > lat !== yj > lat &&
        lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
      ) {
        inside = !inside;
      }
    }
  }
  return inside;
}

async function fetchSeabedNearby(lat: number, lon: number): Promise<SeabedFacts> {
  const geometry = esriPoint(lon, lat);
  const query =
    `query?geometry=${geometry}` +
    "&geometryType=esriGeometryPoint&inSR=4326" +
    `&distance=${Math.round(SEABED_RADIUS_NM * 1852)}&units=esriSRUnit_Meter` +
    "&spatialRel=esriSpatialRelIntersects" +
    "&outFields=NAME,TYPE&returnGeometry=true&outSR=4326&where=1%3D1&f=json";
  const settled = await Promise.allSettled(
    [0, 1, 2].map((layer) => getJson(`${GEBCO_FS}/${layer}/${query}`)),
  );

  // Local tangent plane in nautical miles: x east, y north of the query point.
  const nmPerDeg = (Math.PI / 180) * EARTH_RADIUS_NM;
  const cosLat = Math.cos(toRad(lat));
  const toLocal = (vlat: number, vlon: number): [number, number] => [
    (vlon - lon) * cosLat * nmPerDeg,
    (vlat - lat) * nmPerDeg,
  ];

  let on: { name: string; type: string } | null = null;
  const byName = new Map<string, SeabedNeighbor>();

  for (const result of settled) {
    if (result.status !== "fulfilled") continue;
    const features = featuresOf(result.value);
    if (!features) continue;
    for (const feature of features) {
      const record = asRecord(feature);
      const attrs = attributesOf(feature);
      const geom = record ? asRecord(record.geometry) : null;
      if (!attrs || !geom) continue;
      const rawName = asString(attrs.NAME);
      const rawType = asString(attrs.TYPE);
      if (!rawName || !rawType) continue;
      const name = seabedDisplayName(rawName, rawType);
      const type = rawType.replace(/\(s\)\s*$/, "").trim();

      // Geometry → vertex parts: multipoint points, polyline paths, polygon rings.
      const points = asArray(geom.points);
      const paths = asArray(geom.paths) ?? asArray(geom.rings);
      const parts: number[][][] = [];
      if (points !== null) parts.push(points as number[][]);
      else if (paths !== null) for (const p of paths) parts.push(p as number[][]);
      if (parts.length === 0) continue;

      if (asArray(geom.rings) !== null && pointInRings(lat, lon, parts)) {
        if (on === null) on = { name, type };
        continue;
      }

      // Minimum point-to-segment distance and the bearing to that nearest point.
      let best = Infinity;
      let bx = 0;
      let by = 0;
      for (const part of parts) {
        let prev: [number, number] | null = null;
        for (const vertex of part) {
          if (!Array.isArray(vertex) || vertex.length < 2) continue;
          const cur = toLocal(vertex[1], vertex[0]);
          let cx = cur[0];
          let cy = cur[1];
          let dist = Math.hypot(cx, cy);
          if (prev !== null) {
            const dx = cur[0] - prev[0];
            const dy = cur[1] - prev[1];
            const len2 = dx * dx + dy * dy;
            const t =
              len2 === 0
                ? 0
                : Math.max(0, Math.min(1, -(prev[0] * dx + prev[1] * dy) / len2));
            cx = prev[0] + t * dx;
            cy = prev[1] + t * dy;
            dist = Math.hypot(cx, cy);
          }
          if (dist < best) {
            best = dist;
            bx = cx;
            by = cy;
          }
          prev = cur;
        }
      }
      if (!Number.isFinite(best) || best > SEABED_RADIUS_NM) continue;

      const deg = ((Math.atan2(bx, by) * 180) / Math.PI + 360) % 360;
      const neighbor: SeabedNeighbor = {
        name,
        type,
        distanceNm: Math.max(1, Math.round(best)),
        bearing: COMPASS_16[Math.round(deg / 22.5) % 16],
      };
      const existing = byName.get(name.toLowerCase());
      if (existing === undefined || neighbor.distanceNm < existing.distanceNm) {
        byName.set(name.toLowerCase(), neighbor);
      }
    }
  }

  const nearby = [...byName.values()].sort((a, b) => a.distanceNm - b.distanceNm);
  return { on, nearby };
}

// --- Feature-type glossary ----------------------------------------------------
//
// One-line plain-language definitions of the generic undersea-feature terms,
// paraphrased from IHO B-6 "Standardization of Undersea Feature Names". Keyed
// by the lowercased generic term; a type not listed simply gets no gloss.

const FEATURE_TYPE_DEFS: Readonly<Record<string, string>> = {
  shelf: "the gently sloping seabed running from the coast out to the shelf edge",
  slope: "the seabed descending from the shelf edge toward deep water",
  escarpment: "a long, steep slope separating flat or gently sloping areas of the seabed",
  "abyssal plain": "a flat expanse of deep-ocean floor",
  basin: "a broad depression in the seabed",
  canyon: "a steep-sided valley cut into the continental slope",
  seamount: "an isolated underwater mountain rising 1,000 m or more above the seabed",
  guyot: "a flat-topped seamount",
  knoll: "a small, rounded elevation of the seabed",
  hill: "a small, isolated elevation of the seabed",
  ridge: "a long, narrow elevation of the seabed with steep sides",
  rise: "a broad, gentle elevation of the seabed",
  plateau: "a large, flat-topped elevation of the seabed with one or more steep sides",
  bank: "an elevated area of seabed, shallow relative to its surroundings",
  terrace: "a flat or gently sloping step in the seabed",
  trough: "a long, broad depression with gently sloping sides",
  trench: "a long, narrow and very deep depression with steep sides",
  fan: "a gently sloping, fan-shaped sediment deposit, usually off a canyon mouth",
  valley: "an elongated depression in the seabed",
  "fracture zone": "a long band of irregular seafloor marking a break in the ocean crust",
  seachannel: "a continuous channel running across the seafloor",
  "seamount chain": "a line of seamounts",
  reef: "rock or coral rising near enough the surface to endanger navigation",
  shoal: "a shallow patch of seabed hazardous to surface navigation",
  sill: "a submerged ridge separating two basins",
  spur: "a subordinate ridge protruding from a larger elevation",
  caldera: "a collapsed volcanic crater on the seabed",
  moat: "a ring-shaped depression around the base of a seamount",
  saddle: "a low point along a ridge between two elevations",
  gap: "a break in a ridge connecting deeper areas either side",
  passage: "a break in a ridge connecting deeper areas either side",
  hole: "a small, local depression in the seabed",
  mound: "a small raised feature on the seabed",
  levee: "an embankment bordering a seachannel",
};

/** Glossary lookup for a generic feature term; null when we have no gloss. */
function featureDefinition(type: string | null): string | null {
  if (type === null) return null;
  const key = type.toLowerCase().replace(/\(s\)\s*$/, "").trim();
  return FEATURE_TYPE_DEFS[key] ?? null;
}

// --- Source 2: offshore-wind leases (BOEM) ----------------------------------

async function fetchWindLeases(
  lat: number,
  lon: number,
): Promise<LeaseFact | null> {
  const url =
    "https://services7.arcgis.com/G5Ma95RzqJRPKsWL/arcgis/rest/services/" +
    "Wind_Lease_Boundaries__BOEM_/FeatureServer/8/query?" +
    `geometry=${esriPoint(lon, lat)}` +
    "&geometryType=esriGeometryPoint&inSR=4326" +
    "&distance=50000&units=esriSRUnit_Meter" +
    "&spatialRel=esriSpatialRelIntersects" +
    "&outFields=LEASE_NUMBER,COMPANY&returnGeometry=false&where=1%3D1&f=json";
  const response = await getJsonOrNull(url);
  const features = featuresOf(response);
  if (!features) return null;

  let nearest: string | null = null;
  for (const feature of features) {
    const attrs = attributesOf(feature);
    if (!attrs) continue;
    const number = asString(attrs.LEASE_NUMBER);
    if (!number) continue;
    const company = asString(attrs.COMPANY);
    nearest = company ? `${number} (${company})` : number;
    break;
  }
  return { count: features.length, nearest };
}

// --- Source 3: oil & gas platforms (BOEM/BSEE, Gulf of Mexico) ---------------
//
// The "OCS Drilling Platforms" layer holds each structure as a point. We pull
// standing structures (REMOVAL_DATE IS NULL) within 50 km with geometry and pick
// the nearest by haversine — the same client-side pattern as ports. This layer is
// Gulf of Mexico only, so a point outside the GoM simply returns null (no line).

async function fetchPlatforms(
  lat: number,
  lon: number,
): Promise<PlatformFact | null> {
  const url =
    "https://gis.boem.gov/arcgis/rest/services/BOEM_BSEE/GOA_Layers/MapServer/0/query?" +
    `geometry=${esriPoint(lon, lat)}` +
    "&geometryType=esriGeometryPoint&inSR=4326&outSR=4326" +
    "&distance=50000&units=esriSRUnit_Meter" +
    "&spatialRel=esriSpatialRelIntersects" +
    `&where=${encodeURIComponent("REMOVAL_DATE IS NULL")}` +
    "&outFields=COMPLEX_ID_NUM&returnGeometry=true&f=json";
  const response = await getJsonOrNull(url);
  const features = featuresOf(response);
  if (!features) return null;

  let bestDist = Infinity;
  let bearing: string | null = null;
  for (const feature of features) {
    const geom = asRecord(asRecord(feature)?.geometry);
    if (!geom) continue;
    const plon = asFiniteNumber(geom.x);
    const plat = asFiniteNumber(geom.y);
    if (plat === null || plon === null) continue;
    const dist = haversineNm(lat, lon, plat, plon);
    if (dist < bestDist) {
      bestDist = dist;
      bearing = bearing16(lat, lon, plat, plon);
    }
  }
  return {
    count: features.length,
    distanceNm: bestDist === Infinity ? null : Math.round(bestDist),
    bearing,
  };
}

// --- Source 4: right-whale seasonal management area (NOAA) -------------------

async function fetchWhale(
  lat: number,
  lon: number,
): Promise<{ fact: WhaleFact | null; checked: boolean }> {
  const url =
    "https://coast.noaa.gov/arcgis/rest/services/OceanReports/" +
    "NorthAtlanticRightWhaleSeasonalManagementAreas/MapServer/0/query?" +
    `geometry=${esriPoint(lon, lat)}` +
    "&geometryType=esriGeometryPoint&inSR=4326" +
    "&spatialRel=esriSpatialRelIntersects" +
    "&outFields=location,restrictedPeriod&returnGeometry=false&where=1%3D1&f=json";
  const response = await getJsonOrNull(url);
  const features = featuresOf(response);
  if (!features) return { fact: null, checked: false };
  for (const feature of features) {
    const attrs = attributesOf(feature);
    if (!attrs) continue;
    const location = asString(attrs.location);
    const period = asString(attrs.restrictedPeriod);
    if (location && period) return { fact: { location, period }, checked: true };
  }
  return { fact: null, checked: true };
}

// --- Source 5: nearest commercial port (World Port Index) --------------------
//
// The WPI service holds every port as a point; we pull the ports inside a ±3°
// envelope and pick the nearest by haversine, which avoids the service's own
// Web-Mercator distance units. LATITUDE/LONGITUDE come back as decimal degrees.

async function fetchPort(lat: number, lon: number): Promise<PortFact | null> {
  const box = 3;
  const envelope = encodeURIComponent(
    JSON.stringify({
      xmin: lon - box,
      ymin: lat - box,
      xmax: lon + box,
      ymax: lat + box,
      spatialReference: { wkid: 4326 },
    }),
  );
  const url =
    "https://services9.arcgis.com/j1CY4yzWfwptbTWN/arcgis/rest/services/" +
    "WorldPortIndex_WFL1/FeatureServer/0/query?" +
    `geometry=${envelope}` +
    "&geometryType=esriGeometryEnvelope&inSR=4326" +
    "&spatialRel=esriSpatialRelIntersects" +
    "&outFields=PORT_NAME,LATITUDE,LONGITUDE&returnGeometry=false&where=1%3D1&f=json";
  const response = await getJsonOrNull(url);
  const features = featuresOf(response);
  if (!features) return null;

  let best: PortFact | null = null;
  let bestDist = Infinity;
  for (const feature of features) {
    const attrs = attributesOf(feature);
    if (!attrs) continue;
    const name = asString(attrs.PORT_NAME);
    const plat = asFiniteNumber(attrs.LATITUDE);
    const plon = asFiniteNumber(attrs.LONGITUDE);
    if (!name || plat === null || plon === null) continue;
    const dist = haversineNm(lat, lon, plat, plon);
    if (dist < bestDist) {
      bestDist = dist;
      best = {
        name: titleCase(name),
        distanceNm: Math.round(dist),
        bearing: bearing16(lat, lon, plat, plon),
      };
    }
  }
  return best;
}

// --- Source 6: month climatology (Open-Meteo archives) ----------------------
//
// Pool the same calendar month from the last three full years: daily peak wind
// (kn) from the historical archive, daily peak wave height (m) from the marine
// archive. Wind and wave degrade independently — a missing wave archive still
// yields a wind-only ClimFact. `years` is how many years contributed any data.

function monthBounds(year: number, monthIndex: number): { start: string; end: string } {
  const mm = String(monthIndex + 1).padStart(2, "0");
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  return {
    start: `${year}-${mm}-01`,
    end: `${year}-${mm}-${String(lastDay).padStart(2, "0")}`,
  };
}

/** Pull a named hourly column from an Open-Meteo response as finite numbers. */
function hourlyValues(response: unknown, key: string): number[] {
  const record = asRecord(response);
  const hourly = record ? asRecord(record.hourly) : null;
  const raw = hourly ? asArray(hourly[key]) : null;
  if (!raw) return [];
  const out: number[] = [];
  for (const v of raw) {
    const n = asFiniteNumber(v);
    if (n !== null) out.push(n);
  }
  return out;
}

/**
 * The climatology window: the most recent complete 30 years.
 *
 * THIRTY because quartiles from three years are hostage to three summers — one
 * anomalous season moves them visibly. Thirty is also the WMO's own definition
 * of a climate normal, so the number is one a metocean reviewer recognises.
 *
 * ROLLING rather than the fixed WMO 1991-2020 period, because the objection to a
 * long window is that the climate has moved under it, and a rolling window is
 * the strongest answer available while keeping the sample. It also stays true
 * without anyone remembering to update it.
 *
 * That objection was tested rather than assumed. At 27.5N 94.0W in August,
 * comparing 1995-2004 against 2015-2024 (7,440 hours each), the quartiles moved:
 *
 *     wind   p25 +0.4   p50 +0.5   p75 +0.1   p95 -0.5 kn
 *     wave   p25 +0.04  p50 +0.06  p75 +0.06  p95 -0.10 m
 *
 * The band shifted by less than the rounding this console displays it at, and
 * the p95 fell slightly in both. A recency weighting would have been correcting
 * for something smaller than a whole knot. The climate signal in this region is
 * in the extremes — cyclone intensity, rainfall — not in the interquartile range
 * of hourly August wind, which is what this number is. One site, one month: the
 * conclusion is not global, but it is measured.
 */
const CLIM_YEARS = 30;

/**
 * Waves come from ERA5-Ocean explicitly. The marine archive's DEFAULT model has
 * no history before 2022 — it answers with the right number of slots and every
 * one of them null, which is the worst possible failure because it looks like
 * data. era5_ocean reaches back to at least 1995.
 *
 * Checked for scale before trusting it: over August 2023 the two sources give
 * p25 0.48 vs 0.50 m and p75 0.94 vs 0.92 m. They agree to 2 cm, so the
 * climatology and the operational wave models the band sits behind are on the
 * same scale — which is the property that makes the comparison meaningful at
 * all.
 */
const CLIM_WAVE_MODEL = "era5_ocean";

/** Cache version. Bump when the window, the source or the statistic changes. */
const CLIM_CACHE_KEY = "seastate_clim_v3";
const CLIM_CACHE_MAX = 240;

/**
 * The ERA5 cell a point falls in, to 0.25 degrees.
 *
 * The cache is keyed on this rather than on the clicked coordinate, and that is
 * honest rather than merely thrifty: ERA5 IS a 0.25-degree grid, so two clicks
 * in the same cell were always going to return the same history. Keying on the
 * raw lat/lon would refetch sixty requests to recompute an identical answer.
 */
function climCell(lat: number, lon: number): string {
  const r = (v: number): string => (Math.round(v * 4) / 4).toFixed(2);
  return `${r(lat)},${r(lon)}`;
}

function climCacheRead(key: string): ClimFact | null {
  try {
    const raw = localStorage.getItem(CLIM_CACHE_KEY);
    if (raw === null) return null;
    const blob = asRecord(JSON.parse(raw) as unknown);
    const hit = blob ? asRecord(blob[key]) : null;
    return hit === null ? null : (hit as unknown as ClimFact);
  } catch {
    return null; // storage blocked or corrupt — just refetch
  }
}

function climCacheWrite(key: string, value: ClimFact): void {
  try {
    const raw = localStorage.getItem(CLIM_CACHE_KEY);
    const blob = (raw !== null ? asRecord(JSON.parse(raw) as unknown) : null) ?? {};
    blob[key] = value as unknown as Record<string, unknown>;
    // Crude cap. These records are ~150 bytes, so this is about storage
    // hygiene, not pressure; insertion order is close enough to LRU here.
    const keys = Object.keys(blob);
    if (keys.length > CLIM_CACHE_MAX) {
      for (const k of keys.slice(0, keys.length - CLIM_CACHE_MAX)) delete blob[k];
    }
    localStorage.setItem(CLIM_CACHE_KEY, JSON.stringify(blob));
  } catch {
    /* full or blocked — the value still applies for this session */
  }
}

/**
 * Run `fn` over `items` at most `limit` at a time.
 *
 * Sixty requests fired at once would queue behind the browser's per-host cap
 * anyway, but they would also arrive at our relay as sixty simultaneous
 * upstream fetches. Eight at a time keeps the burst civil and still finishes a
 * cold cell in a few seconds.
 */
async function pool<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const i = next++;
      if (i >= items.length) return;
      out[i] = await fn(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

/** p0..p100 of an already-SORTED array, rounded to keep the cache small. */
function sketch(sorted: number[], dp: number): number[] {
  if (sorted.length === 0) return [];
  const f = Math.pow(10, dp);
  const out = new Array<number>(101);
  for (let i = 0; i <= 100; i++) {
    const idx = Math.min(sorted.length - 1, Math.round((i / 100) * (sorted.length - 1)));
    out[i] = Math.round(sorted[idx] * f) / f;
  }
  return out;
}

async function fetchClimatology(
  lat: number,
  lon: number,
  monthIndex: number,
): Promise<ClimFact | null> {
  // Last COMPLETE year: the current one is partial, and a half-finished season
  // would weight the quartiles toward whichever months have happened.
  const lastYear = new Date().getUTCFullYear() - 1;
  const firstYear = lastYear - CLIM_YEARS + 1;
  const cacheKey = `${climCell(lat, lon)}|${monthIndex}|${firstYear}-${lastYear}`;
  const cached = climCacheRead(cacheKey);
  if (cached !== null) return cached;

  const coords = `latitude=${lat.toFixed(4)}&longitude=${lon.toFixed(4)}`;
  const years: number[] = [];
  for (let y = firstYear; y <= lastYear; y++) years.push(y);

  // Through our own relay (server/openmeteo.go), like every other Open-Meteo
  // call: the subscription key must not reach a browser.
  const urls: string[] = [];
  for (const year of years) {
    const { start, end } = monthBounds(year, monthIndex);
    urls.push(
      "/api/om/archive?" +
        `${coords}&start_date=${start}&end_date=${end}` +
        "&hourly=wind_speed_10m&wind_speed_unit=kn&timezone=UTC",
    );
    urls.push(
      "/api/om/marine?" +
        `${coords}&start_date=${start}&end_date=${end}` +
        `&hourly=wave_height&timezone=UTC&models=${CLIM_WAVE_MODEL}`,
    );
  }

  const responses = await pool(urls, 8, (u) => getJsonOrNull(u));

  const wind: number[] = [];
  const wave: number[] = [];
  // The same hours again, split at the window's midpoint. Kept separately so the
  // record can be asked whether it has MOVED, not only what it averages to —
  // which is the question an operator planning a season actually has.
  const half = Math.floor(years.length / 2);
  const windEarly: number[] = [];
  const windLate: number[] = [];
  const waveEarly: number[] = [];
  const waveLate: number[] = [];
  let windYears = 0;
  let waveYears = 0;
  for (let i = 0; i < years.length; i++) {
    const w = hourlyValues(responses[i * 2], "wind_speed_10m");
    const h = hourlyValues(responses[i * 2 + 1], "wave_height");
    if (w.length > 0) windYears++;
    if (h.length > 0) waveYears++;
    wind.push(...w);
    wave.push(...h);
    if (i < half) {
      windEarly.push(...w);
      waveEarly.push(...h);
    } else {
      windLate.push(...w);
      waveLate.push(...h);
    }
  }
  if (wind.length === 0) return null;

  const mkHalf = (
    y0: number,
    y1: number,
    wnd: number[],
    wv: number[],
  ): ClimHalf | null => {
    if (wnd.length === 0) return null;
    wnd.sort((a, b) => a - b);
    wv.sort((a, b) => a - b);
    return { firstYear: y0, lastYear: y1, windPct: sketch(wnd, 1), wavePct: sketch(wv, 2) };
  };
  const early = mkHalf(firstYear, firstYear + half - 1, windEarly, waveEarly);
  const late = mkHalf(firstYear + half, lastYear, windLate, waveLate);

  wind.sort((a, b) => a - b);
  let waveP25M: number | null = null;
  let waveP75M: number | null = null;
  if (wave.length > 0) {
    wave.sort((a, b) => a - b);
    waveP25M = Math.round(quantile(wave, 0.25) * 10) / 10;
    waveP75M = Math.round(quantile(wave, 0.75) * 10) / 10;
  }

  const fact: ClimFact = {
    windP25Kn: Math.round(quantile(wind, 0.25)),
    windP75Kn: Math.round(quantile(wind, 0.75)),
    waveP25M,
    waveP75M,
    years: windYears,
    waveYears,
    firstYear,
    lastYear,
    monthName: MONTH_NAMES[monthIndex] ?? "",
    early,
    late,
  };
  climCacheWrite(cacheKey, fact);
  return fact;
}

// --- Public API -------------------------------------------------------------

/**
 * Assemble the location primer for a point and month. Every source is fetched
 * concurrently and degrades to null on its own; the returned record is always
 * well-formed even when every source failed. `monthIndex` is 0-11.
 */
export async function fetchPrimer(
  lat: number,
  lon: number,
  monthIndex: number,
): Promise<PrimerFacts> {
  const [
    regionResult,
    seabedResult,
    windLeasesResult,
    platformsResult,
    whaleResult,
    portResult,
    climResult,
  ] = await Promise.allSettled([
    fetchRegion(lat, lon),
    fetchSeabedNearby(lat, lon),
    fetchWindLeases(lat, lon),
    fetchPlatforms(lat, lon),
    fetchWhale(lat, lon),
    fetchPort(lat, lon),
    fetchClimatology(lat, lon, monthIndex),
  ]);

  const whale =
    whaleResult.status === "fulfilled"
      ? whaleResult.value
      : { fact: null, checked: false };

  const region: RegionFacts =
    regionResult.status === "fulfilled"
      ? regionResult.value
      : { feature: null, featureType: null, sea: null };
  const seabed: SeabedFacts =
    seabedResult.status === "fulfilled"
      ? seabedResult.value
      : { on: null, nearby: [] };

  // Marine Regions gives the canonical "on" feature when its polygon covers
  // the point; a containing NCEI polygon is the fallback. Nearby features drop
  // whichever name the site is already on.
  const feature = region.feature ?? seabed.on?.name ?? null;
  const featureType =
    region.feature !== null ? region.featureType : seabed.on?.type ?? null;
  const nearbySeabed = seabed.nearby
    .filter(
      (n) => feature === null || n.name.toLowerCase() !== feature.toLowerCase(),
    )
    .slice(0, 3);

  return {
    feature,
    featureType,
    sea: region.sea,
    nearbySeabed,
    windLeases:
      windLeasesResult.status === "fulfilled" ? windLeasesResult.value : null,
    platforms:
      platformsResult.status === "fulfilled" ? platformsResult.value : null,
    whale: whale.fact,
    whaleChecked: whale.checked,
    port: portResult.status === "fulfilled" ? portResult.value : null,
    clim: climResult.status === "fulfilled" ? climResult.value : null,
  };
}

// --- Prose ------------------------------------------------------------------
//
// Compose the non-null facts into at most two short paragraphs. Nothing is
// invented and no "unknown" filler appears: a missing fact simply drops its
// clause. Every number and name is deterministic from `facts`.

/**
 * Render the primer as flowing prose — up to two `<p>` paragraphs, with
 * `<strong>` on the feature and port names. `jurisdictionName` (e.g. "United
 * States EEZ"), when given, sets the geographic frame. Returns "" when there is
 * nothing at all to say.
 */
export function buildPrimerHtml(
  facts: PrimerFacts,
  jurisdictionName: string | null,
): string {
  // Paragraph 1 — place and infrastructure.
  const p1: string[] = [];

  const feature = facts.feature ? escapeHtml(facts.feature) : null;
  const juris = jurisdictionName ? escapeHtml(jurisdictionName) : null;
  const sea = facts.sea ? escapeHtml(facts.sea) : null;
  const frames: string[] = [];
  if (sea) frames.push(`in the ${sea}`);
  if (juris) frames.push(`in the ${juris}`);
  if (feature) {
    const def = featureDefinition(facts.featureType);
    let sentence = `This site sits on the <strong>${feature}</strong>`;
    if (def) sentence += ` — ${def} —`;
    if (frames.length > 0) sentence += (def ? " " : ", ") + frames.join(", ");
    p1.push(sentence + ".");
  } else if (frames.length > 0) {
    p1.push(`This site lies ${frames.join(", ")}.`);
  }

  if (facts.nearbySeabed.length > 0) {
    const items = facts.nearbySeabed.map((n, i) => {
      const word = COMPASS_WORD[n.bearing] ?? n.bearing;
      let item = `the <strong>${escapeHtml(n.name)}</strong>`;
      // With no on-feature, the nearest neighbor carries the one glossary line.
      if (i === 0 && !feature) {
        const def = featureDefinition(n.type);
        if (def) item += ` (${def})`;
      }
      return `${item} about ${n.distanceNm} nm to the ${word}`;
    });
    const list =
      items.length === 1
        ? items[0]
        : items.length === 2
          ? `${items[0]} and ${items[1]}`
          : `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
    // Gazetteer positions are representative marks — a broad feature like a
    // shelf or plateau can extend well past its mark, to and beyond this site.
    p1.push(`Charted nearby: ${list} (distances to each feature’s charted mark).`);
  }

  const infra: string[] = [];
  let infraSingle = true;
  if (facts.windLeases && facts.windLeases.count > 0) {
    const n = facts.windLeases.count;
    infra.push(`${n} offshore wind ${n === 1 ? "lease" : "leases"}`);
    if (n !== 1) infraSingle = false;
  }
  if (infra.length > 0) {
    const subject = infra.join(" and ");
    const verb = infra.length === 1 && infraSingle ? "lies" : "lie";
    p1.push(`${subject} ${verb} within ${fmtKm(50)}.`);
  }
  if (facts.platforms && facts.platforms.count > 0) {
    const n = facts.platforms.count;
    let sentence =
      `${n} oil & gas ${n === 1 ? "platform" : "platforms"} ${n === 1 ? "lies" : "lie"} within ${fmtKm(50)}`;
    if (facts.platforms.distanceNm !== null && facts.platforms.bearing !== null) {
      const word = COMPASS_WORD[facts.platforms.bearing] ?? facts.platforms.bearing;
      sentence += `, the nearest about ${facts.platforms.distanceNm} nm to the ${word}`;
    }
    p1.push(sentence + ".");
  }

  if (facts.whale) {
    const loc = escapeHtml(facts.whale.location);
    const period = escapeHtml(facts.whale.period);
    p1.push(
      `A North Atlantic right-whale seasonal speed restriction (10 kn, ${period}) ` +
        `applies here in the ${loc} management area.`,
    );
  }

  // Paragraph 2 — access and typical conditions.
  const p2: string[] = [];

  if (facts.port) {
    const word = COMPASS_WORD[facts.port.bearing] ?? facts.port.bearing;
    p2.push(
      `The nearest commercial port is <strong>${escapeHtml(facts.port.name)}</strong>, ` +
        `about ${facts.port.distanceNm} nm to the ${word}.`,
    );
  }


  // The month's climatology used to end this paragraph, as "this area typically
  // sees X-Y kn winds". It is now its own Seasonal normal block beside the
  // briefing, and a band behind the wind and wave tracks (main.ts) — it answers
  // an operational question and the rest of this prose answers a geographic one.
  // The facts still carry `clim`; this builder simply no longer prints it.

  const paragraphs: string[] = [];
  if (p1.length > 0) paragraphs.push(`<p>${p1.join(" ")}</p>`);
  if (p2.length > 0) paragraphs.push(`<p>${p2.join(" ")}</p>`);
  return paragraphs.join("");
}
